import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  UNSAFE_LocationContext as LocationContext,
  useLocation,
  useNavigationType,
  useResolvedPath,
} from 'react-router-dom';
import {
  SidebarItem,
  useSidebarOpenState,
  useSidebarPinState,
} from '@backstage/core-components';
import type { Theme } from '@material-ui/core';
import { Tooltip, useMediaQuery } from '@material-ui/core';

/** @public */
export interface SidebarNavItemProps {
  /**
   * Overrides core's own location match. `true` lights the item up on any
   * page, `false` keeps it dark even when the location starts with `to`, and
   * leaving it out keeps core's prefix match.
   */
  active?: boolean;
  /**
   * The glyph in front of the label, 24 wide in both sidebar states. The type
   * is whatever core's SidebarItem takes, because the item passes it on.
   */
  icon: ComponentProps<typeof SidebarItem>['icon'];
  /** The label, also the accessible name and the tooltip text. */
  text: string;
  /** The route the item links to. */
  to: string;
}

/**
 * Renders its children under a virtual location equal to `to`, so core's item
 * itself decides "active" and sets its selected class and
 * `aria-current="page"`. Core ignores an `aria-current` passed in, and the
 * selected class name is hashed in production, so neither can be set from
 * outside.
 */
const LocationAt = ({ children, to }: { children: ReactNode; to: string }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { pathname } = useResolvedPath(to);
  const value = useMemo(
    () => ({
      location: { ...location, hash: '', pathname, search: '' },
      navigationType,
    }),
    [location, navigationType, pathname],
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

/**
 * Whether hovering or focusing an item may show its label as a tooltip.
 * Compact, only while the sidebar stays compact: from 1280 core expands it on
 * hover and the label shows itself. Open, only when the label is truncated.
 * `isLabelTruncated` measures the DOM, so it is only called when open.
 */
const canShowTooltip = ({
  isLabelTruncated,
  isOpen,
  staysCompact,
}: {
  isLabelTruncated: () => boolean;
  isOpen: boolean;
  staysCompact: boolean;
}): boolean => (isOpen ? isLabelTruncated() : staysCompact);

/**
 * Finds the label inside the item the event came from, as the element without
 * element children whose text is the label, and reports whether it is cut
 * off. Core's label class is hashed in production, so it cannot be selected.
 */
const isLabelTruncatedAt = (
  target: EventTarget | null,
  text: string,
): boolean => {
  const item = target instanceof Element ? target.closest('a') : null;
  const label = item
    ? [...item.querySelectorAll('*')].find(
        element =>
          element.childElementCount === 0 && element.textContent === text,
      )
    : undefined;
  return label instanceof HTMLElement && label.scrollWidth > label.clientWidth;
};

/**
 * One navigation entry of the sidebar menu.
 *
 * @remarks
 * A thin wrapper around core's `SidebarItem`. It shows its label as a tooltip
 * on the right on hover and keyboard focus: compact only while the sidebar
 * stays compact (below 1280), open only when the label is truncated, and
 * never on mobile. `active` lets the caller decide which item is current
 * instead of core's prefix match on the location.
 *
 * @public
 */
export const SidebarNavItem = ({
  active,
  icon,
  text,
  to,
}: SidebarNavItemProps) => {
  const { isOpen } = useSidebarOpenState();
  const { isMobile } = useSidebarPinState();
  // The same boundary core uses to keep the sidebar compact.
  const staysCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );
  const [isTooltipOpen, setTooltipOpen] = useState(false);

  // The tooltip is controlled, so MUI does not close it on Escape. WCAG 1.4.13
  // asks that extra content on hover or focus can be dismissed without moving.
  useEffect(() => {
    if (!isTooltipOpen) {
      return;
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setTooltipOpen(false);
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isTooltipOpen]);

  // Link replaces the history entry when the location equals the target, and
  // the virtual location of an active item always does, so it pushes
  // explicitly. `end` stops core's prefix match from lighting up an item that
  // is not active.
  const item = (
    <SidebarItem
      icon={icon}
      text={text}
      to={to}
      {...(active === true && { replace: false })}
      {...(active === false && { end: true })}
    />
  );

  const handleTooltipOpen = (event: { target: EventTarget | null }) => {
    const isAllowed = canShowTooltip({
      isLabelTruncated: () => isLabelTruncatedAt(event.target, text),
      isOpen,
      staysCompact,
    });
    if (isAllowed) {
      setTooltipOpen(true);
    }
  };

  const content = isMobile ? (
    item
  ) : (
    // A node title, not a string: MUI copies a string title into the native
    // `title` attribute while closed, and the browser would show that itself.
    // Interactive, so the tooltip stays open while the pointer is on it.
    <Tooltip
      interactive
      onClose={() => setTooltipOpen(false)}
      onOpen={handleTooltipOpen}
      open={isTooltipOpen}
      placement="right"
      title={<>{text}</>}
    >
      {item}
    </Tooltip>
  );

  return active === true ? <LocationAt to={to}>{content}</LocationAt> : content;
};
